import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Button,
  Card,
  Typography,
  Skeleton,
  Box,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Close, Search, Visibility } from '@mui/icons-material';
import TablePagination from '@mui/material/TablePagination';
import { GETAvailableTechnicians } from '../../../services/technician';
import { ITechnician } from '../../../utils/entities/interface';
import { AssignTechnician } from '../../../services/booking';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  capitalizeWords,
  formatPhoneNumber,
  formatTechnicianType,
} from '../../../utils/helper';
import { AssignInspector } from '../../../services/sellOldAC';
import { useNavigate } from 'react-router-dom';

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const AssignTechnicianModal = ({
  isOpen,
  onClose,
  bookingId,
  onReload,
  isEnquiry,
}: {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  onReload: () => void;
  isEnquiry: boolean;
}) => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [technicians, setTechnicians] = useState<ITechnician[] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [typeFilter, setTypeFilter] = useState<'ALL' | 'ACD' | 'FC'>('ALL');

  const fetchTechnicians = async (query = '') => {
    setLoading(true);

    try {
      const payload: any = {
        page: page + 1,
        limit: rowsPerPage,
        searchQuery: query,
      };

      if (typeFilter !== 'ALL') {
        payload.type = typeFilter;
      }

      const response = await GETAvailableTechnicians(payload);

      setTechnicians(response?.data || []);
      setTotalTechnician(response?.count || 0);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setTechnicians([]);
      setTotalTechnician(0);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchTechnicians = useCallback(
    debounce((query: string) => {
      fetchTechnicians(query);
    }, 500),
    [page, rowsPerPage, typeFilter],
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetchTechnicians(searchTerm);
    }
  }, [
    searchTerm,
    page,
    rowsPerPage,
    typeFilter,
    isOpen,
    debouncedFetchTechnicians,
  ]);

  const handleTypeChange = (value: 'ALL' | 'ACD' | 'FC') => {
    setTypeFilter(value);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTechieAssign = async (
    event: React.MouseEvent<HTMLButtonElement>,
    techieId: string,
  ) => {
    event.preventDefault();

    if (isEnquiry) {
      try {
        const result = await AssignInspector(bookingId, techieId, 'INSPECTION');

        if (result === 200) {
          showSnackbar('Inspector assigned successfully!', 'success');

          setTimeout(() => {
            onReload();
            onClose();
          }, 600);
        } else {
          showSnackbar('Assignment Failed!', 'error');
        }
      } catch (err) {
        // Surface the real reason (e.g. technician unavailable) instead of a
        // generic message. Missing inspection schedule no longer errors.
        showSnackbar(
          err instanceof Error ? err.message : 'Error assigning technician.',
          'error',
        );
      }
    } else {
      try {
        const result = await AssignTechnician(bookingId, techieId);

        if (result === 200) {
          showSnackbar('Technician assigned successfully!', 'success');

          setTimeout(() => {
            onReload();
            onClose();
          }, 600);
        } else {
          showSnackbar('Assignment Failed!', 'error');
        }
      } catch {
        showSnackbar('Error assigning technician.', 'error');
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ borderRadius: 10, overflow: 'hidden' }}
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'auto',
          backgroundColor: '#1C1C1C ',
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2',
          borderRadius: 10,
          overflow: 'auto',
          backgroundColor: '#1C1C1C ',
        }}
      >
        {SnackbarComponent}

        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#1C1C1C ',
            padding: '16px',
          }}
        >
          Assign Technician
          <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ bgcolor: '#1E1E1E', color: 'white', padding: 3 }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <TextField
              variant="outlined"
              placeholder="Search Technician"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#B0B0B0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '50%',
                input: { color: '#E0E0E0' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#232323',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#232323',
                  },
                  '&:hover fieldset': {
                    borderColor: '#232323',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#232323',
                  },
                },
              }}
            />

            <FormControl sx={{ width: 120 }} variant="outlined">
              <InputLabel sx={{ color: '#B0B0B0' }}>Type</InputLabel>

              <Select
                name="type"
                value={typeFilter}
                onChange={(e) =>
                  handleTypeChange(e.target.value as 'ALL' | 'ACD' | 'FC')
                }
                label="Type"
                sx={{
                  color: '#E0E0E0',
                  bgcolor: '#232323',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#232323',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#232323',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#232323',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#B0B0B0',
                  },
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ACD">NVK</MenuItem>
                <MenuItem value="FC">FC</MenuItem>
              </Select>
            </FormControl>
          </div>

          {loading ? (
            Array.from(new Array(5)).map((_, index) => (
              <Card
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#2A2A2A',
                  color: 'white',
                  padding: 2,
                  borderRadius: 2,
                  border: 'none',
                  mb: 1,
                }}
              >
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={100} height={15} />
                <Skeleton variant="text" width={80} height={15} />
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="rectangular" width={80} height={30} />
              </Card>
            ))
          ) : (
            <>
              {Array.isArray(technicians) && technicians.length > 0 ? (
                technicians.map((tech: ITechnician) => (
                  <Card
                    key={tech._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#232323',
                      color: 'white',
                      padding: 2,
                      borderRadius: 2,
                      border: 'none',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" color="#8A8D91" width="20%">
                      {tech.name}
                    </Typography>

                    <Typography variant="body2" color="#8A8D91" width="20%">
                      {formatPhoneNumber(tech.phoneNumber)}
                    </Typography>

                    <Typography variant="body2" color="#8A8D91" width="15%">
                      {capitalizeWords(tech.position)}
                    </Typography>

                    <Typography variant="body2" color="#8A8D91" width="10%">
                      {(tech as any)?.type
                        ? formatTechnicianType((tech as any).type)
                        : '-'}
                    </Typography>

                    <Typography variant="body2" color="#8A8D91">
                      {tech.level}
                    </Typography>

                    <IconButton
                      sx={{ color: '#7B1FA2' }}
                      onClick={() => navigate(`/technicians/${tech._id}`)}
                    >
                      <Visibility />
                    </IconButton>

                    <Button
                      variant="outlined"
                      color="success"
                      onClick={(e) => handleTechieAssign(e, tech._id)}
                    >
                      Assign
                    </Button>
                  </Card>
                ))
              ) : (
                <Typography
                  color="gray"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: 300,
                    margin: 'auto',
                  }}
                >
                  No Available technicians found.
                </Typography>
              )}
            </>
          )}

          <TablePagination
            rowsPerPageOptions={[2, 5, 10, 25, 50]}
            component="div"
            count={totalTechnician}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default AssignTechnicianModal;

interface ModalConfirmationProps {
  open: boolean;
  handleClose: () => void;
  technician: string;
}

export const ModalNotification = ({
  open,
  handleClose,
  technician,
}: ModalConfirmationProps) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ borderRadius: 4 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <DialogContent
          sx={{
            backgroundColor: '#181818',
            padding: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: '2px solid #7B1FA2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          />

          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            {technician + ' technician is assigned and will assist you shortly'}
          </Typography>
        </DialogContent>
      </Box>
    </Dialog>
  );
};
